import type { AdminHome, AdminItem, LocalizedPair } from '@diora/contracts';
import { editablePair } from '@diora/contracts';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PageHeader } from '@/components/app-shell';
import { OptionalBilingualField } from '@/components/bilingual';
import { ImagePicker } from '@/components/image-picker';
import { Alert, Badge, Button, Card, Select, SkeletonRows, Switch } from '@/components/ui';
import { apiGet, apiPatch, useAdminMutation } from '@/hooks/admin';
import { qk } from '@/lib/query';
import { useAuth } from '@/providers/auth';
import { useLocale } from '@/providers/locale';

const URL = '/admin/content/home';
const MAX_FEATURED = 8;

const SECTION_KEYS = [
  ['aboutPreview', 'showAboutPreview'],
  ['featuredItems', 'showFeaturedItems'],
  ['activeOffers', 'showActiveOffers'],
  ['branchesPreview', 'showBranchesPreview'],
  ['faqPreview', 'showFaqPreview'],
  ['contactStrip', 'showContactStrip'],
] as const;

export function HomeContentPage() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can } = useAuth();
  const canEdit = can('OWNER', 'EDITOR');

  const query = useQuery({
    queryKey: qk.home,
    queryFn: () => apiGet<AdminHome>(URL),
  });
  const home = query.data?.data;

  const items = useQuery({
    queryKey: qk.items({ featuredPicker: true }),
    queryFn: () => apiGet<AdminItem[]>('/admin/menu/items', { page: 1, pageSize: 200 }),
  });

  const [heading, setHeading] = useState<LocalizedPair>({ ar: '', en: '' });
  const [subheading, setSubheading] = useState<LocalizedPair>({ ar: '', en: '' });
  const [teaser, setTeaser] = useState<LocalizedPair>({ ar: '', en: '' });
  const [heroImageId, setHeroImageId] = useState<string | null>(null);
  const [heroImage, setHeroImage] = useState<{ id: string; width: number; height: number; thumbUrl: string; url: string } | null>(null);
  const [sections, setSections] = useState<Record<string, boolean>>({});
  const [featured, setFeatured] = useState<string[]>([]);

  // Seed the form once the record arrives, and again after a save replaces it.
  useEffect(() => {
    if (!home) return;
    setHeading(editablePair(home.hero.heading));
    setSubheading(editablePair(home.hero.subheading));
    setTeaser(editablePair(home.aboutTeaser));
    setHeroImageId(home.hero.imageId);
    setSections({ ...home.sections });
    setFeatured(home.featuredItems.map((item) => item.id));
  }, [home]);

  const save = useAdminMutation(
    () =>
      apiPatch(URL, {
        expectedVersion: home?.version ?? 1,
        heroHeadingAr: heading.ar,
        heroHeadingEn: heading.en,
        heroSubAr: subheading.ar,
        heroSubEn: subheading.en,
        aboutTeaserAr: teaser.ar,
        aboutTeaserEn: teaser.en,
        heroImageId,
        showAboutPreview: sections.aboutPreview,
        showFeaturedItems: sections.featuredItems,
        showActiveOffers: sections.activeOffers,
        showBranchesPreview: sections.branchesPreview,
        showFaqPreview: sections.faqPreview,
        showContactStrip: sections.contactStrip,
        featuredItemIds: featured,
      }),
    { successMessage: t('common.saved'), invalidate: [qk.home, qk.dashboard] },
  );

  if (query.isLoading || !home) return <SkeletonRows rows={6} />;

  const catalogue = items.data?.data ?? [];
  const available = catalogue.filter((item) => !featured.includes(item.id));

  function move(id: string, delta: number) {
    setFeatured((current) => {
      const from = current.indexOf(id);
      const to = from + delta;
      if (from < 0 || to < 0 || to >= current.length) return current;
      const next = [...current];
      const [moving] = next.splice(from, 1);
      if (moving) next.splice(to, 0, moving);
      return next;
    });
  }

  return (
    <>
      <PageHeader
        width="narrow"
        title={t('home.title')}
        description={t('home.subtitle')}
        actions={
          canEdit && (
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
              {t('common.save')}
            </Button>
          )
        }
      />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title={t('home.heroHeading')}>
          <div className="flex flex-col gap-4">
            <OptionalBilingualField
              label={t('home.heroHeading')}
              value={heading}
              onChange={setHeading}
              maxLength={140}
              disabled={!canEdit}
            />
            <OptionalBilingualField
              label={t('home.heroSubheading')}
              value={subheading}
              onChange={setSubheading}
              multiline
              rows={2}
              maxLength={300}
              disabled={!canEdit}
            />
            <ImagePicker
              label={t('home.heroImage')}
              surface="offer"
              value={heroImage ? { ...heroImage, storageKey: '', alt: { ar: '', en: '' } } : null}
              disabled={!canEdit}
              onChange={(picked) => {
                setHeroImageId(picked?.id ?? null);
                setHeroImage(picked);
              }}
            />
            <OptionalBilingualField
              label={t('home.aboutTeaser')}
              value={teaser}
              onChange={setTeaser}
              multiline
              maxLength={500}
              disabled={!canEdit}
            />
          </div>
        </Card>

        <div className="flex flex-col gap-4">
          <Card title={t('home.sections')}>
            <div className="flex flex-col gap-3">
              {SECTION_KEYS.map(([key]) => (
                <Switch
                  key={key}
                  checked={sections[key] ?? false}
                  disabled={!canEdit}
                  onChange={(checked) => setSections((current) => ({ ...current, [key]: checked }))}
                  label={t(`home.section${key[0]!.toUpperCase()}${key.slice(1)}` as 'home.sectionAboutPreview')}
                />
              ))}
            </div>
          </Card>

          <Card title={t('home.featured')} description={t('home.featuredHint')}>
            {home.hiddenFeaturedCount > 0 && (
              <Alert tone="warning">
                {t('home.featuredHidden', { count: home.hiddenFeaturedCount })}
              </Alert>
            )}

            <ul className="mt-3 flex flex-col gap-2">
              {featured.map((id, index) => {
                const item = catalogue.find((candidate) => candidate.id === id);
                const known = home.featuredItems.find((candidate) => candidate.id === id);
                return (
                  <li key={id} className="flex flex-wrap items-center gap-2">
                    <span className="min-w-0 flex-1">
                      {item ? item.name[locale] || item.name.ar || item.name.en : known?.name[locale] ?? id}
                    </span>
                    {known && !known.publiclyVisible && (
                      <Badge tone="warning" dot>
                        {t('offers.notVisible')}
                      </Badge>
                    )}
                    {canEdit && (
                      <>
                        <Button size="sm" iconOnly variant="ghost" aria-label={t('common.moveUp')} disabled={index === 0} onClick={() => move(id, -1)}>
                          <ChevronUp className="dio-icon" aria-hidden="true" />
                        </Button>
                        <Button size="sm" iconOnly variant="ghost" aria-label={t('common.moveDown')} disabled={index === featured.length - 1} onClick={() => move(id, 1)}>
                          <ChevronDown className="dio-icon" aria-hidden="true" />
                        </Button>
                        <Button
                          size="sm"
                          iconOnly
                          variant="ghost"
                          aria-label={t('common.remove')}
                          onClick={() => setFeatured((current) => current.filter((candidate) => candidate !== id))}
                        >
                          <Trash2 className="dio-icon" aria-hidden="true" />
                        </Button>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>

            {canEdit && featured.length < MAX_FEATURED && (
              <Select
                className="mt-3"
                value=""
                aria-label={t('home.addFeatured')}
                onChange={(event) => {
                  if (event.target.value) setFeatured((current) => [...current, event.target.value]);
                }}
              >
                <option value="">{t('home.addFeatured')}</option>
                {available.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name[locale] || item.name.ar || item.name.en}
                  </option>
                ))}
              </Select>
            )}
          </Card>
        </div>
      </div>
    </>
  );
}
